import { createContext, useState, useContext, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { AuthContext, YearContext } from "./context";
import { fetchVisionBoardLayout, saveVisionBoardLayout } from "../api";
import Toast from "../Components/Elements/Toast";

const VisionBoardContext = createContext(null);

export const VisionBoardProvider = ({ children }) => {
  const { selectedYear } = useContext(YearContext);
  const { userData, loggedIn, setUserData } = useContext(AuthContext);
  const [toast, setToast] = useState({ message: "", type: "" });

  const goals = Array.isArray(userData?.goals) ? userData.goals : [];

  const [moodboardFilteredData, setMoodboardFilteredData] = useState([]);

  const columns = 6;

  // Initialize layout as null - will be set in useEffect
  const [layout, setLayout] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Create stable references for goals to prevent unnecessary re-renders
  const goalsKey = useMemo(() => {
    return goals.map(g => `${g.id}-${g.year}-${g.img || ''}`).sort().join(',');
  }, [goals]);

  // Create stable reference for visionBoardLayouts to detect changes
  const visionBoardLayoutsKey = useMemo(() => {
    const layouts = userData?.visionBoardLayouts || [];
    return layouts.map(vbl => `${vbl.year}-${vbl.id}`).sort().join(',');
  }, [userData?.visionBoardLayouts]);

  // Load layout from userData or API when component mounts or year changes
  useEffect(() => {
    const loadLayout = async () => {
      if (!selectedYear || !loggedIn) return;

      // Update filtered data first
      const filteredGoals = selectedYear
        ? goals.filter((goal) => goal.year === selectedYear)
        : goals;

      console.log('🎯 VisionBoard Debug:', {
        selectedYear,
        totalGoals: goals.length,
        filteredGoals: filteredGoals.length,
        filteredGoalIds: filteredGoals.map(g => g.id),
        goalsWithImages: filteredGoals.filter(g => g.img).length
      });

      setMoodboardFilteredData(filteredGoals);

      // Build default layout based on current filtered goals
      // Use goal.id as key, not array index!
      const defaultLayout = filteredGoals.map((goal, i) => ({
        i: goal.id.toString(),
        x: i % columns,
        y: Math.floor(i / columns),
        w: 1,
        h: 1,
      }));

      // First, try to use the layout from userData (already loaded on login)
      const visionBoardLayouts = userData?.visionBoardLayouts || [];
      const savedLayoutData = visionBoardLayouts.find(
        (vbl) => vbl.year === selectedYear
      );

      if (savedLayoutData && savedLayoutData.layout) {
        // Use the layout from userData - NO LOADING needed!
        const savedLayout = savedLayoutData.layout;

        console.log('💾 Found saved layout:', {
          savedLayoutLength: savedLayout.length,
          savedLayoutIds: savedLayout.map(item => item.i)
        });

        // Sync layout with current goals: only keep layout items that match current goal IDs
        const currentGoalIds = filteredGoals.map(g => g.id.toString());
        const syncedLayout = savedLayout.filter(item => currentGoalIds.includes(item.i));

        // Add any new goals that aren't in the saved layout
        const existingLayoutIds = syncedLayout.map(item => item.i);
        const newGoals = filteredGoals.filter(goal => !existingLayoutIds.includes(goal.id.toString()));

        // Find next available position for new goals
        let maxY = syncedLayout.length > 0 ? Math.max(...syncedLayout.map(item => item.y)) : 0;
        let maxX = syncedLayout.length > 0 ? Math.max(...syncedLayout.filter(item => item.y === maxY).map(item => item.x)) : -1;

        newGoals.forEach(goal => {
          maxX++;
          if (maxX >= columns) {
            maxX = 0;
            maxY++;
          }
          syncedLayout.push({
            i: goal.id.toString(),
            x: maxX,
            y: maxY,
            w: 1,
            h: 1,
          });
        });

        console.log('📐 Layout Sync:', {
          savedLayoutItems: savedLayout.length,
          syncedLayoutItems: syncedLayout.length,
          newGoalsAdded: newGoals.length,
          finalLayout: syncedLayout.map(item => item.i)
        });

        setLayout(syncedLayout.length > 0 ? syncedLayout : defaultLayout);
      } else {
        // Only show loading if we need to fetch from API
        try {
          setIsLoading(true);
          const savedLayout = await fetchVisionBoardLayout(selectedYear);
          if (savedLayout && savedLayout.length > 0) {
            setLayout(savedLayout);
          } else {
            setLayout(defaultLayout);
          }
        } catch (error) {
          console.error("Error loading layout:", error);
          setToast({ message: "Failed to load layout", type: "error" });
          setLayout(defaultLayout);
        } finally {
          setIsLoading(false);
        }
      }
    };

    loadLayout();
  }, [selectedYear, loggedIn, goalsKey, visionBoardLayoutsKey]);

  // Save layout to API when it changes
  useEffect(() => {
    const saveLayout = async () => {
      if (!selectedYear || isLoading || !layout) return;

      try {
        await saveVisionBoardLayout(selectedYear, layout);

        // Update userData to keep visionBoardLayouts in sync
        // Use a callback to get the latest userData without adding it as a dependency
        setUserData((currentUserData) => {
          if (!currentUserData) return currentUserData;

          const visionBoardLayouts = currentUserData.visionBoardLayouts || [];
          const existingIndex = visionBoardLayouts.findIndex(
            vbl => vbl.year === selectedYear
          );

          // Check if the layout actually changed to avoid unnecessary updates
          if (existingIndex >= 0) {
            const existingLayout = visionBoardLayouts[existingIndex].layout;
            if (JSON.stringify(existingLayout) === JSON.stringify(layout)) {
              return currentUserData; // No change needed
            }
          }

          const updatedLayout = {
            year: selectedYear,
            layout: layout,
            user_id: currentUserData.id,
            // Keep the id if it exists
            ...(existingIndex >= 0 && { id: visionBoardLayouts[existingIndex].id })
          };

          let updatedVisionBoardLayouts;
          if (existingIndex >= 0) {
            updatedVisionBoardLayouts = [...visionBoardLayouts];
            updatedVisionBoardLayouts[existingIndex] = updatedLayout;
          } else {
            updatedVisionBoardLayouts = [...visionBoardLayouts, updatedLayout];
          }

          return {
            ...currentUserData,
            visionBoardLayouts: updatedVisionBoardLayouts
          };
        });
      } catch (error) {
        console.error("Error saving layout:", error);
        setToast({ message: "Failed to save layout", type: "error" });
      }
    };

    // Debounce the save operation
    const timeoutId = setTimeout(saveLayout, 1000);
    return () => clearTimeout(timeoutId);
  }, [layout, selectedYear, isLoading, setUserData]);

  return (
    <VisionBoardContext.Provider
      value={{ layout, setLayout, moodboardFilteredData, isLoading }}
    >
      {toast.message && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ message: "", type: "" })}
        />
      )}
      {children}
    </VisionBoardContext.Provider>
  );
};

export const useVisionBoard = () => {
  const context = useContext(VisionBoardContext);
  if (context === null) {
    throw new Error("useVisionBoard must be used within a VisionBoardProvider");
  }
  return context;
};

VisionBoardProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
